import React from "react";
import BusinessCardDisplay from "./BusinessCardDisplay";
import BusinessCardMinimalistDisplay from "./BusinessCardMinimalistDisplay";
import BusinessCardCorporateDisplay from "./BusinessCardCorporateDisplay";
import BusinessCardCreativeDisplay from "./BusinessCardCreativeDisplay";
import BusinessCardTechDisplay from "./BusinessCardTechDisplay";
import BusinessCardVintageDisplay from "./BusinessCardVintageDisplay";
import BusinessCardLuxuryDisplay from "./BusinessCardLuxuryDisplay";
import BusinessCardModernDisplay from "./BusinessCardModernDisplay";
import BusinessCardElegantDisplay from "./BusinessCardElegantDisplay";
import BusinessCardBoldDisplay from "./BusinessCardBoldDisplay";
import BusinessCardVibrantDisplay from "./BusinessCardVibrantDisplay";
import BusinessCardMedicalDisplay from "./BusinessCardMedicalDisplay";
import { LinkItem } from "./LinkManager";
import {
  BusinessCardBooking,
  BusinessCardTemplate,
} from "../../types/BusinessCardTypes";

export interface BusinessCardData {
  jobTitle: string;
  description?: string;
  links: LinkItem[];
  uploadedFiles?: Array<{
    fileName: string;
    cdnPath: string;
    displayName: string;
  }>;
  allowVCardDownload?: boolean;
  booking?: BusinessCardBooking;
}

export interface UserData {
  user_identification: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  avatar: string;
}

interface BusinessCardRendererProps {
  userData: UserData;
  businessCardData: BusinessCardData;
  customerIdentification: string;
  entityIdentification: string;
  className?: string;
  fallbackTemplate?: BusinessCardTemplate;
  isPreview?: boolean;
  processId?: string;
  entityData?: {
    entity_name?: string;
    entity_description?: string;
    entity_logo?: string;
  };
  entityPreferences?: {
    business_card_form?: {
      template?: BusinessCardTemplate;
      enableUserRegistration?: boolean;
      enableSaveContact?: boolean;
      showFooter?: boolean;
    };
    cdn_logo_project_path?: string;
  };
}

const BusinessCardRenderer: React.FC<BusinessCardRendererProps> = ({
  userData,
  businessCardData,
  customerIdentification,
  entityIdentification,
  className = "",
  fallbackTemplate = "default",
  isPreview = false,
  processId,
  entityData,
  entityPreferences,
}) => {
  const selectedTemplate: BusinessCardTemplate =
    entityPreferences?.business_card_form?.template || fallbackTemplate;

  const enableUserRegistration =
    entityPreferences?.business_card_form?.enableUserRegistration || false;

  const allowVCardDownload =
    entityPreferences?.business_card_form?.enableSaveContact ||
    businessCardData.allowVCardDownload;

  const showFooter = entityPreferences?.business_card_form?.showFooter;

  const resolvedBusinessCardData = {
    ...businessCardData,
    allowVCardDownload,
    showFooter,
  };

  const presentationProps = {
    userData,
    businessCardData: resolvedBusinessCardData,
    companyLogo:
      entityPreferences?.cdn_logo_project_path || entityData?.entity_logo,
    companyName: entityData?.entity_name,
    companyDescription: entityData?.entity_description,
    className,
    isPreview,
    processId,
    enableUserRegistration,
    entityIdentification,
    ownerIdentification: customerIdentification,
  };

  const renderTemplate = () => {
    switch (selectedTemplate) {
      case "minimalist":
        return <BusinessCardMinimalistDisplay {...presentationProps} />;
      case "corporate":
        return <BusinessCardCorporateDisplay {...presentationProps} />;
      case "creative":
        return <BusinessCardCreativeDisplay {...presentationProps} />;
      case "tech":
        return <BusinessCardTechDisplay {...presentationProps} />;
      case "luxury":
        return <BusinessCardLuxuryDisplay {...presentationProps} />;
      case "vintage":
        return <BusinessCardVintageDisplay {...presentationProps} />;
      case "modern":
        return <BusinessCardModernDisplay {...presentationProps} />;
      case "elegant":
        return <BusinessCardElegantDisplay {...presentationProps} />;
      case "bold":
        return <BusinessCardBoldDisplay {...presentationProps} />;
      case "vibrant":
        return <BusinessCardVibrantDisplay {...presentationProps} />;
      case "medical":
        return <BusinessCardMedicalDisplay {...presentationProps} />;
      case "default":
      default:
        return <BusinessCardDisplay {...presentationProps} />;
    }
  };

  return renderTemplate();
};

export default BusinessCardRenderer;
